import { useEffect, useState } from 'react';
import { CircleCheck, KeyRound, Mail, ShieldAlert, ShieldCheck, TriangleAlert } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@components/ui/dialog';
import { BarButton } from '@generated/components/form/ActionBar';
import { TextField } from '@generated/components/form/Field';
import { TurnstileWidget } from '@generated/components/TurnstileWidget';
import { confirmCancelRequest, requestCancelCode } from '@generated/server/cancelRequest';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after a card was cancelled, so the caller can release its own blocked state. */
  onCancelled: () => void;
}

type Step = 'identify' | 'code' | 'done';

const HELP_LINE = 'אם לא הצלחתם להשלים את התהליך, יש לפנות למזכירות סטודנטים בפקולטה ל��נדסה.';
const MAX_ATTEMPTS = 4;
const COOLDOWN_SECONDS = 300; // 5 minutes
const STORAGE_KEY = 'cancel_request_lockout_until';

export function CancelRequestDialog({ open, onOpenChange, onCancelled }: Props) {
  const [step, setStep] = useState<Step>('identify');
  const [requestNumber, setRequestNumber] = useState('');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [maskedEmail, setMaskedEmail] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

  // Security attempts tracking & 5-minute lockout timer
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [lockoutRemaining, setLockoutRemaining] = useState(0);

  // Load persistent lockout from localStorage on open
  useEffect(() => {
    if (!open || typeof window === 'undefined') return;
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const until = parseInt(stored, 10);
        const now = Date.now();
        if (until > now) {
          setLockoutRemaining(Math.ceil((until - now) / 1000));
        } else {
          localStorage.removeItem(STORAGE_KEY);
          setLockoutRemaining(0);
        }
      }
    } catch {
      // ignore
    }
  }, [open]);

  // Live countdown timer
  useEffect(() => {
    if (lockoutRemaining <= 0) return;
    const timer = setInterval(() => {
      setLockoutRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          try {
            localStorage.removeItem(STORAGE_KEY);
          } catch {
            // ignore
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [lockoutRemaining]);

  const reset = () => {
    setStep('identify');
    setRequestNumber('');
    setEmail('');
    setCode('');
    setMaskedEmail(null);
    setNotice(null);
    setFailed(false);
    setBusy(false);
    setTurnstileToken(null);
    setFailedAttempts(0);
  };

  const close = (next: boolean) => {
    onOpenChange(next);
    if (!next) reset();
  };

  const formatTimer = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const sendCode = async () => {
    if (!turnstileToken) {
      setFailed(true);
      setNotice('יש להשלים את אימות האבטחה ("אני לא רובוט") לפני שליחת הקוד.');
      return;
    }

    if (!requestNumber.trim() || !email.trim()) {
      setFailed(true);
      setNotice('יש להזין מספר בקשה וכתובת מייל.');
      return;
    }

    if (lockoutRemaining > 0) {
      setFailed(true);
      setNotice(`המערכת נעולה מטעמי אבטחה. נסו שוב בעוד ${formatTimer(lockoutRemaining)}.`);
      return;
    }

    setBusy(true);
    setNotice(null);
    try {
      const res = await requestCancelCode({
        data: {
          requestNumber,
          email,
          turnstileToken: turnstileToken || undefined,
        },
      });
      setNotice(res?.message ?? null);
      setFailed(res?.stage !== 'sent');
      if (res?.stage === 'sent') {
        setMaskedEmail(res.maskedEmail);
        setCode('');
        setFailedAttempts(0);
        setStep('code');
      }
    } catch (error) {
      console.error('Failed to request a cancellation code', error);
      setFailed(true);
      setNotice('לא ניתן היה לשלוח את הקוד כרגע. נסו שוב בעוד מספר רגעים.');
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    if (lockoutRemaining > 0) {
      setFailed(true);
      setNotice(`המערכת נעולה עקב מספר ניסיונות שגויים. שחרור בעוד ${formatTimer(lockoutRemaining)}.`);
      return;
    }

    if (!code.trim() || code.trim().length < 6) {
      setFailed(true);
      setNotice('יש להזין את קוד האימות המלא בן 6 הספרות.');
      return;
    }

    setBusy(true);
    setNotice(null);
    try {
      const res = await confirmCancelRequest({ data: { requestNumber, email, code } });
      setNotice(res?.message ?? null);
      setFailed(res?.stage !== 'cancelled');

      if (res?.stage === 'cancelled') {
        try {
          localStorage.removeItem(STORAGE_KEY);
        } catch {
          // ignore
        }
        setStep('done');
        onCancelled();
      } else if (res?.stage === 'wrong_code') {
        const nextAttempts = failedAttempts + 1;
        setFailedAttempts(nextAttempts);

        if (nextAttempts >= MAX_ATTEMPTS || res.message?.includes('נעולה') || res.message?.includes('נוצל')) {
          const until = Date.now() + COOLDOWN_SECONDS * 1000;
          try {
            localStorage.setItem(STORAGE_KEY, String(until));
          } catch {
            // ignore
          }
          setLockoutRemaining(COOLDOWN_SECONDS);
          setCode('');
          setNotice(`בוצעו ${MAX_ATTEMPTS} ניסיונות שגויים. מטעמי אבטחה, האפשרות ננעלה ל-5 דקות והקוד בוטל.`);
        } else {
          const remaining = MAX_ATTEMPTS - nextAttempts;
          if (remaining === 1) {
            setNotice('קוד שגוי! אזהרה: נותר ניסיון 1 אחרון לפני נעילת המערכת ל-5 דקות.');
          } else {
            setNotice(`קוד אימות שגוי. נותרו ${remaining} ניסיונות מתוך ${MAX_ATTEMPTS}.`);
          }
        }
      } else if (res?.stage === 'expired' || res?.stage === 'locked') {
        if (res.message?.includes('נעולה') || res.message?.includes('נוצל') || res.message?.includes('4 ניסיונות')) {
          const until = Date.now() + COOLDOWN_SECONDS * 1000;
          try {
            localStorage.setItem(STORAGE_KEY, String(until));
          } catch {
            // ignore
          }
          setLockoutRemaining(COOLDOWN_SECONDS);
        }
        setStep('identify');
      }
    } catch (error) {
      console.error('Failed to cancel the request', error);
      setFailed(true);
      setNotice('הביטול לא הושלם. נסו שוב בעוד מספר רגעים.');
    } finally {
      setBusy(false);
    }
  };

  const isSendDisabled = busy || !turnstileToken || !requestNumber.trim() || !email.trim() || lockoutRemaining > 0;
  const isConfirmDisabled = busy || !code.trim() || code.trim().length < 6 || lockoutRemaining > 0;

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent dir="rtl" className="border-[hsl(var(--rule))] sm:max-w-lg">
        <DialogHeader className="text-start">
          <DialogTitle className="flex items-center gap-2 text-start text-lg font-medium">
            {step === 'done' ? (
              <CircleCheck className="size-5 text-[hsl(var(--ok))]" aria-hidden="true" />
            ) : (
              <ShieldCheck className="size-5 text-muted-foreground" aria-hidden="true" />
            )}
            {step === 'done' ? 'הבקשה בוטלה' : 'ביטול בקשה קיימת'}
          </DialogTitle>
          <DialogDescription className="text-start leading-relaxed">
            {step === 'identify'
              ? 'לאימות הזהות יש להזין את מספר הבקשה ואת כתובת המייל שאיתה הוגשה, ולאשר את אימות האבטחה.'
              : step === 'code'
                ? `הוזן קוד בן 6 ספרות שנשלח לכתובת ${maskedEmail ?? 'הרשומה בבקשה'}. הקוד תקף ל-10 דקות.`
                : 'הבקשה סומנה כמבוטלת. ניתן להמשיך ולהגיש בקשה חדשה.'}
          </DialogDescription>
        </DialogHeader>

        {/* Lockout Warning Banner */}
        {lockoutRemaining > 0 && (
          <div className="flex items-start gap-2.5 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-xs text-destructive">
            <ShieldAlert className="mt-0.5 size-4.5 shrink-0" />
            <div>
              <p className="font-bold">נעילת אבטחה פעילה (4 ניסיונות שגויים)</p>
              <p className="mt-0.5">
                המערכת ננעלה למשך 5 דקות למניעת ניחוש קודים. זמן שחרור משוער: <strong>{formatTimer(lockoutRemaining)}</strong>.
              </p>
            </div>
          </div>
        )}

        {notice ? (
          <p
            role={failed ? 'alert' : 'status'}
            className={
              failed
                ? 'flex items-start gap-2 border border-destructive bg-[hsl(var(--surface-1))] px-3 py-2 text-[13px] leading-relaxed text-foreground'
                : 'flex items-start gap-2 border border-[hsl(var(--rule))] bg-[hsl(var(--tint-mint))] px-3 py-2 text-[13px] leading-relaxed text-foreground'
            }
          >
            {failed ? (
              <TriangleAlert className="mt-0.5 size-4 shrink-0 text-destructive" aria-hidden="true" />
            ) : (
              <Mail className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            )}
            <span className="min-w-0">
              {notice}
              {failed && step === 'identify' ? <span className="block pt-1 text-muted-foreground">{HELP_LINE}</span> : null}
            </span>
          </p>
        ) : null}

        {step === 'identify' ? (
          <div className="grid gap-1">
            <TextField
              id="cancel-request-number"
              label="מספר בקשה"
              required
              value={requestNumber}
              onChange={setRequestNumber}
              disabled={lockoutRemaining > 0}
              placeholder="לדוגמה R-0001"
              hint="המספר מופיע במייל האישור שהתקבל לאחר ההגשה."
            />
            <TextField
              id="cancel-email"
              label="כתובת מייל"
              required
              type="email"
              dir="ltr"
              value={email}
              onChange={setEmail}
              disabled={lockoutRemaining > 0}
              placeholder="name@mail.tau.ac.il"
            />
            <div className="pt-2">
              <TurnstileWidget
                onVerify={(token) => setTurnstileToken(token)}
                onExpire={() => setTurnstileToken(null)}
              />
            </div>
          </div>
        ) : null}

        {step === 'code' ? (
          <div className="space-y-2">
            <TextField
              id="cancel-code"
              label="קוד אימות"
              required
              value={code}
              onChange={(next) => setCode(next.replace(/\D/g, '').slice(0, 6))}
              inputMode="numeric"
              dir="ltr"
              maxLength={6}
              disabled={lockoutRemaining > 0}
              placeholder="000000"
              hint={
                failedAttempts > 0
                  ? `ניסיון ${failedAttempts} מתוך ${MAX_ATTEMPTS} (נעילה ל-5 דקות ב-4 שגיאות)`
                  : 'לא התקבל קוד? ניתן לחזור אחורה ולבקש קוד חדש.'
              }
            />
          </div>
        ) : null}

        <div className="mt-2 flex flex-wrap justify-end gap-2">
          {step === 'identify' ? (
            <>
              <BarButton tone="ghost" onClick={() => close(false)} disabled={busy}>
                ביטול
              </BarButton>
              <BarButton tone="primary" onClick={sendCode} disabled={isSendDisabled}>
                <KeyRound className="size-4" aria-hidden="true" />
                {busy ? 'שולח קוד…' : 'שליחת קוד אימות'}
              </BarButton>
            </>
          ) : null}

          {step === 'code' ? (
            <>
              <BarButton tone="ghost" onClick={() => setStep('identify')} disabled={busy || lockoutRemaining > 0}>
                חזרה
              </BarButton>
              <BarButton tone="primary" onClick={confirm} disabled={isConfirmDisabled}>
                <ShieldCheck className="size-4" aria-hidden="true" />
                {busy ? 'מאמת…' : 'אישור ביטול הבקשה'}
              </BarButton>
            </>
          ) : null}

          {step === 'done' ? (
            <BarButton tone="primary" onClick={() => close(false)}>
              סגירה
            </BarButton>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
